import { useEffect, useMemo, useState } from 'react'
import {
  CalendarClock,
  ClipboardList,
  Fish,
  GraduationCap,
  Hourglass,
  KeyRound,
  ListChecks,
  Newspaper,
  RefreshCcwDot,
  RefreshCw,
  ShieldCheck,
  Siren,
  Sparkles,
  Trophy,
  UserRoundPlus,
  Users,
} from 'lucide-react'
import Button from '../../components/Button'
import DataTable from '../../components/DataTable'
import FileField from '../../components/FileField'
import { InfoNote } from '../../components/Form'
import FormRow from '../../components/FormRow'
import LabelChip from '../../components/LabelChip'
import Meter from '../../components/Meter'
import Modal from '../../components/Modal'
import PageHeader from '../../components/PageHeader'
import StatusBadge from '../../components/StatusBadge'
import { useToast } from '../../components/toastContext'
import PageLoader from '../../components/PageLoader'
import { strings as S } from './strings'
import { getLicense, updateLicense } from './licenseService'
import './LicensePage.css'
import { serverText } from '../../lib/i18n'

const num = (value) => (value == null ? S.unavailable : value.toLocaleString('en-US'))

/** How much of a whole is spent, 0–100, or null when either half is unknown. */
const spent = (used, total) => (used == null || !total ? null : Math.min(100, (used / total) * 100))

/**
 * The bar's own verdict on itself: it starts green and darkens as it fills.
 * Past 60% consumed somebody should look; past 90% the licence is effectively
 * spent and somebody has to act. Seats and days share the scale — a licence at
 * 92% of its seats is the same finding as one 92% through its term.
 */
const toneFor = (percentUsed) => {
  if (percentUsed == null) return 'neutral'
  if (percentUsed > 90) return 'danger'
  if (percentUsed > 60) return 'warning'
  return 'success'
}

/**
 * الرخصة — Views/AdminLicenseManagement/Index.cshtml.
 *
 * The legacy prints eleven fields as eleven equal cells and leaves the reader to
 * work out which of them is the one that matters. Two of them are: how many DAYS
 * are left, and how many SEATS are left. Both are fractions of a whole the page
 * already knows, so each reads as a remainder over a meter — the figure somebody
 * acts on, the bar saying how close it is to the end, and the flat facts
 * (start, duration, expiry / seats, consumed) demoted under the mark they
 * explain. Everything else — client, type, operation, licensed modules — is
 * identity, so it sits in the card's head rather than competing as a tile.
 *
 * The licence blob in the demo database is encrypted, so a field with nothing
 * behind it renders the legacy placeholder and its meter shows an empty track.
 */
/**
 * The eight licence features, drawn with the icon the SIDEBAR uses for the same area.
 *
 * A licence grants صياد or السياسات; the reader recognises those by the mark beside them in
 * the navigation, so repeating it here costs nothing and makes the list scannable rather
 * than eight lines of text. Keys are the `LicenseFeatureSet` member names, including the
 * enum's own misspelling of Gamification.
 */
const FEATURE_ICONS = {
  Lms: GraduationCap,
  Policies: ShieldCheck,
  Phishing: Fish,
  SecurityReports: Siren,
  Assessments: ClipboardList,
  AwarenessContent: Newspaper,
  Gamififcation: Trophy,
  Surveys: ListChecks,
}

const FEATURE_ORDER = Object.keys(FEATURE_ICONS)

/**
 * A DELIBERATE tint per licence type, rather than LabelChip's hash of the name.
 *
 * The five types are a scale, not a set of unrelated labels: open grants everything, closed
 * grants nothing, and the three in between limit by time, by seats, or by both. Hashing the
 * name gave `Open` and `Close` whatever colours their letters happened to land on — two
 * opposite meanings with no visual relation. These read in order.
 */
const TYPE_TONES = {
  Open: 0, // emerald — unrestricted
  Close: 7, // slate — inert; the one type that is never valid
  TimeStamp: 2, // sky — bounded by time
  NumberOfUsers: 4, // violet — bounded by seats
  NumberOfUsersAndTimeStamp: 3, // indigo — bounded by both
}

/** The operation, tinted by what it does to the licence you already have. */
const OPERATION_META = {
  new: { icon: Sparkles, tone: 'new' },
  update: { icon: RefreshCcwDot, tone: 'update' },
  renew: { icon: RefreshCw, tone: 'renew' },
  'public-key': { icon: KeyRound, tone: 'key' },
}

export default function LicensePage() {
  const toast = useToast()
  const [license, setLicense] = useState(null)
  const [updateOpen, setUpdateOpen] = useState(false)
  const [file, setFile] = useState(null)
  /* What the picked file SAYS, read before anything is sent. Null until a file parses. */
  const [preview, setPreview] = useState(null)
  const [renewOption, setRenewOption] = useState('no-copy')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const load = () => getLicense().then(setLicense)

  // Reopening on a previous file's preview would invite applying the wrong licence twice.
  const closeUpdate = () => {
    setUpdateOpen(false)
    setFile(null)
    setPreview(null)
    setError('')
    setRenewOption('no-copy')
  }

  useEffect(() => {
    load()
  }, [])

  const columns = useMemo(
    () => [
      {
        key: 'licenseType',
        header: S.history.licenseType,
        render: (row) => (
          <span className="license__type">
            {/* A deliberate tint per type rather than LabelChip's hash of the name — see
                TYPE_TONES. The hash also changed colour with the LANGUAGE, because it ran on
                the localised string. */}
            <LabelChip tone={TYPE_TONES[row.licenseTypeKey]}>{row.licenseType}</LabelChip>
          </span>
        ),
      },
      {
        key: 'startDate',
        header: S.history.startDate,
        render: (row) => (
          <span className="license__data" dir="ltr">
            {row.startDate}
          </span>
        ),
      },
      {
        key: 'duration',
        header: S.history.durationDays,
        /* `duration` and `usersNumber`, which is what the service maps. The columns read
           `durationDays` and `totalAccounts` — names nothing supplies — so both showed a
           dash on every row whatever the licence said. */
        render: (row) => <span className="license__data">{num(row.duration)}</span>,
      },
      {
        key: 'usersNumber',
        header: S.history.totalAccounts,
        render: (row) => <span className="license__data">{num(row.usersNumber)}</span>,
      },
    ],
    [],
  )

  /**
   * Applying a new licence term.
   *
   * The endpoint takes the document's TEXT, not an upload, so the file is read here. The
   * modal stays open on failure — the licence is the thing that decides how many people
   * can use the product, and closing it over a message nobody read is how a failed renewal
   * gets mistaken for a successful one.
   */
  /**
   * READ THE FILE WHEN IT IS PICKED, not when it is submitted.
   *
   * The legacy screen does the same and for the same reason: a licence decides how many
   * people can use the product and for how long, and the administrator applying one should
   * see whose licence it is and what it grants BEFORE it replaces the current term. It also
   * catches the two mistakes that otherwise only surface as a server refusal — the wrong
   * file entirely, and a `new` licence being applied where an update belongs.
   *
   * The submit stays disabled until a file parses, exactly as the legacy disables it.
   */
  const pick = async (selected) => {
    setFile(selected)
    setPreview(null)
    setError('')

    if (!selected) return

    let parsed
    try {
      parsed = JSON.parse(await selected.text())
    } catch {
      setError(S.update.notJson)
      return
    }

    const info = parsed?.LicenseInfo
    if (!info || typeof info !== 'object' || Array.isArray(info)) {
      setError(S.update.noLicenseInfo)
      return
    }

    setPreview({
      clientName: info.ClientName ?? '',
      operationType: String(info.OperationType ?? '').toLowerCase(),
      licenseType: info.LicenseType ?? '',
      startDate: info.StartDate ?? '',
      usersNumber: info.NumberOfUsers ?? 0,
      duration: info.Duration ?? 0,
      featureSet: info.FeatureSet ?? '',
    })
  }

  const submit = async (event) => {
    event.preventDefault()
    if (!file || !preview) {
      setError(S.update.emptyFile)
      return
    }

    setSubmitting(true)
    setError('')

    let content
    try {
      content = await file.text()
    } catch {
      setSubmitting(false)
      setError(S.update.emptyFile)
      return
    }

    const outcome = await updateLicense({
      file: content,
      // Only a RENEWAL has anything to carry over; an update keeps the licence it edits.
      copyOldUsers: preview.operationType === 'renew' && renewOption === 'copy',
    })
    setSubmitting(false)

    if (!outcome.ok) {
      // The server's own words: which key failed to verify, or why the term was refused.
      setError(outcome.message || S.update.failed)
      return
    }

    setUpdateOpen(false)
    setFile(null)
    setPreview(null)
    toast.success(serverText(outcome.message) || S.update.success)
    // The seat counts and the expiry all move when a term is applied.
    load()
  }

  if (!license) {
    return (
      <>
        <PageHeader title={S.title} subtitle={S.subtitle} />
        <PageLoader />
      </>
    )
  }

  const daysUsed =
    license.daysLeft == null || !license.duration
      ? null
      : Math.min(100, ((license.duration - license.daysLeft) / license.duration) * 100)
  const seatsUsed = spent(license.consumedAccounts, license.usersNumber)

  /* The licence's own verdict on itself, from the figure the legacy already
     prints. `daysLeft` is floored at 0 once the term has lapsed. */
  const state =
    license.daysLeft === 0
      ? { label: S.state.expired, tone: 'danger' }
      : /* Only the bar's last band. The 60% mark is where a bar should start
           warning about its own fill; it is far too early to tell an admin the
           licence is nearly over — 278 of 1,095 days is nine months. */
        toneFor(daysUsed) === 'danger'
        ? { label: S.state.expiring, tone: 'warning' }
        : { label: S.state.active, tone: 'success' }


  /* The features the file GRANTS, or null for "not limited to any". `FeatureSet` is a
     ';'-separated list of `LicenseFeatureSet` names; the generator normalises the separator
     and the product splits on it the same way. */
  const currentGranted = !license.featureSet?.trim()
    ? null
    : new Set(
        license.featureSet
          .split(';')
          .map((name) => name.trim())
          .filter(Boolean),
      )

  const previewGranted = !preview?.featureSet?.trim()
    ? null
    : new Set(
        preview.featureSet
          .split(';')
          .map((name) => name.trim())
          .filter(Boolean),
      )

  const operation = OPERATION_META[preview?.operationType] ?? OPERATION_META.update

  const readings = [
    {
      id: 'days',
      icon: CalendarClock,
      label: S.fields.daysLeft,
      value: license.daysLeft,
      scale: license.duration,
      percent: daysUsed,
      facts: [
        { label: S.fields.startDate, value: license.startDate, ltr: true },
        { label: S.fields.expirationDate, value: license.expirationDate, ltr: true },
        { label: S.fields.duration, value: num(license.duration) },
      ],
    },
    {
      id: 'seats',
      icon: Users,
      label: S.fields.remainingAccounts,
      value: license.remainingAccounts,
      scale: license.usersNumber,
      percent: seatsUsed,
      facts: [
        { label: S.fields.usersNumber, value: num(license.usersNumber) },
        { label: S.fields.consumedAccounts, value: num(license.consumedAccounts) },
      ],
    },
  ]

  return (
    <>
      <PageHeader
        title={S.title}
        subtitle={S.subtitle}
        actions={
          <Button onClick={() => setUpdateOpen(true)}>
            <RefreshCw size={16} strokeWidth={1.75} />
            {S.update.trigger}
          </Button>
        }
      />

      <section className="licence" aria-label={S.subtitle}>
        <header className="licence__head">
          <span className="licence__mark" aria-hidden="true">
            <KeyRound size={22} strokeWidth={1.75} />
          </span>

          <div className="licence__identity">
            <h2 className="licence__client">
              <bdi>{license.companyName ?? S.unavailable}</bdi>
            </h2>
            <p className="licence__terms">
              <span className="licence__term">
                <bdi className="licence__term-label">{S.fields.licenseType}</bdi>
                <bdi className="licence__term-value">{license.licenseType || S.unavailable}</bdi>
              </span>
              <span className="licence__term">
                <bdi className="licence__term-label">{S.fields.operationType}</bdi>
                <bdi className="licence__term-value">
                  {S.operations[license.operationType] ?? license.operationType ?? S.unavailable}
                </bdi>
              </span>
            </p>
          </div>

          <StatusBadge tone={state.tone}>{state.label}</StatusBadge>
        </header>

        {/* The two countdowns. The figure is what is LEFT — the number somebody
            acts on — and the bar underneath is how much has been SPENT, filling
            and darkening toward the end of the licence. They point opposite ways
            on purpose, so the percentage is spelled out beside the bar and
            neither can be read as the other. */}
        <div className="licence__readings">
          {readings.map((reading) => {
            const Icon = reading.icon
            return (
              <article key={reading.id} className={`reading reading--${toneFor(reading.percent)}`}>
                <div className="reading__head">
                  <span className="reading__chip" aria-hidden="true">
                    <Icon size={18} strokeWidth={1.9} />
                  </span>
                  <bdi className="reading__label">{reading.label}</bdi>
                </div>

                <p className="reading__figure" dir="ltr">
                  <span className="reading__value">{num(reading.value)}</span>
                  <span className="reading__scale">/ {num(reading.scale)}</span>
                </p>

                <div className="reading__gauge">
                  <Meter
                    value={reading.percent}
                    tone={toneFor(reading.percent)}
                    label={reading.label}
                  />
                  <span className="reading__spent">
                    <bdi>{S.fields.spent}</bdi>
                    <span dir="ltr">
                      {reading.percent == null ? S.unavailable : `${Math.round(reading.percent)}%`}
                    </span>
                  </span>
                </div>

                <dl className="reading__facts">
                  {reading.facts.map((fact) => (
                    <div key={fact.label} className="reading__fact">
                      <dt>
                        <bdi>{fact.label}</bdi>
                      </dt>
                      <dd {...(fact.ltr ? { dir: 'ltr' } : null)}>
                        <bdi>{fact.value ?? S.unavailable}</bdi>
                      </dd>
                    </div>
                  ))}
                </dl>
              </article>
            )
          })}
        </div>

        <div className="license__features">
          <div className="license__features-head">
            <bdi className="license__features-title">{S.update.preview.featuresIncluded}</bdi>
            <bdi className="license__features-count">
              {currentGranted === null
                ? S.update.preview.featuresAll
                : S.update.preview.featuresSome(currentGranted.size, FEATURE_ORDER.length)}
            </bdi>
          </div>

          <ul className="license__features-list">
            {FEATURE_ORDER.map((key) => {
              const Icon = FEATURE_ICONS[key]
              const on = currentGranted === null || currentGranted.has(key)

              return (
                <li
                  className={`license__feature${on ? '' : ' license__feature--off'}`}
                  key={key}
                  title={on ? undefined : S.update.preview.excluded}
                >
                  <Icon size={14} strokeWidth={1.75} aria-hidden="true" />
                  <bdi>{S.features[key] ?? key}</bdi>
                </li>
              )
            })}
          </ul>
        </div>

      </section>

      <DataTable
        title={S.history.title}
        columns={columns}
        rows={license.history}
        controls={false}
        getRowId={(_row, index) => index}
        initialSort={{ key: 'startDate', dir: 'desc' }}
      />

      <Modal
        open={updateOpen}
        title={S.update.trigger}
        onClose={closeUpdate}
        footer={
          <Button type="submit" form="license-update-form" disabled={submitting || !preview}>
            {submitting ? S.update.submitting : S.update.submit}
          </Button>
        }
      >
        <form id="license-update-form" className="license__form" onSubmit={submit} noValidate>
          <FormRow label={S.update.fileLabel} error={error} hint={S.update.fileHint}>
            {(id) => (
              <FileField
                id={id}
                name="LicenseFile"
                /* The generator writes `.json`; older files carry `.license`. Both are the
                   same document, so both are offered rather than one being refused. */
                accept=".json,.license,application/json"
                onChange={pick}
              />
            )}
          </FormRow>

          {preview && (
            <section className={`license__preview license__preview--${operation.tone}`}>
              {/* The banner answers the two questions asked in that order: whose licence is
                  this, and what will it do to mine. */}
              <header className="license__preview-head">
                <span className="license__preview-mark" aria-hidden="true">
                  <operation.icon size={18} strokeWidth={1.75} />
                </span>
                <span className="license__preview-who">
                  {/* THE BDI IS INSIDE, not on the styled block. `<bdi>` carries an implicit
                      `dir="auto"`, so a Latin client name in an Arabic panel made the element
                      itself LTR — and `text-align: start` then resolved to the opposite edge
                      from the line beneath it. The span takes the panel's direction and the
                      bdi keeps doing its one job, isolating the name. */}
                  <span className="license__preview-client">
                    <bdi>{preview.clientName}</bdi>
                  </span>
                  <span className="license__preview-op">
                    <bdi>{S.operations[preview.operationType] ?? preview.operationType}</bdi>
                  </span>
                </span>
                <span className="license__preview-type">
                  <LabelChip tone={TYPE_TONES[preview.licenseType]}>
                    {S.licenseTypes[preview.licenseType] ?? preview.licenseType}
                  </LabelChip>
                </span>
              </header>

              <dl className="license__preview-grid">
                {[
                  [CalendarClock, S.update.preview.startDate, preview.startDate],
                  [Hourglass, S.update.preview.duration, S.update.preview.days(preview.duration)],
                  [UserRoundPlus, S.update.preview.usersNumber, preview.usersNumber.toLocaleString('en-US')],
                ].map(([Icon, label, value]) => (
                  <div className="license__preview-row" key={label}>
                    <dt>
                      <Icon size={14} strokeWidth={1.75} aria-hidden="true" />
                      <bdi>{label}</bdi>
                    </dt>
                    <dd>
                      <bdi>{value}</bdi>
                    </dd>
                  </div>
                ))}
              </dl>

              {/* EVERY feature, not just the granted ones. A licence is as much about what it
                  withholds, and a list of three chips does not tell you the other five are
                  missing. An empty FeatureSet is not "no features" — the product reads it as
                  unrestricted (`HasFeature` returns true for an empty set), so everything is
                  drawn as included and the caption says why. */}
              <div className="license__features">
                <div className="license__features-head">
                  <bdi className="license__features-title">{S.update.preview.featuresIncluded}</bdi>
                  <bdi className="license__features-count">
                    {previewGranted === null
                      ? S.update.preview.featuresAll
                      : S.update.preview.featuresSome(previewGranted.size, FEATURE_ORDER.length)}
                  </bdi>
                </div>

                <ul className="license__features-list">
                  {FEATURE_ORDER.map((key) => {
                    const Icon = FEATURE_ICONS[key]
                    const on = previewGranted === null || previewGranted.has(key)

                    return (
                      <li
                        className={`license__feature${on ? '' : ' license__feature--off'}`}
                        key={key}
                        title={on ? undefined : S.update.preview.excluded}
                      >
                        <Icon size={14} strokeWidth={1.75} aria-hidden="true" />
                        <bdi>{S.features[key] ?? key}</bdi>
                      </li>
                    )
                  })}
                </ul>
              </div>

              {/* The server refuses these two anyway; saying so here saves a round trip and
                  names the file the administrator should have picked instead. */}
              {(preview.operationType === 'new' || preview.operationType === 'public-key') && (
                <InfoNote>
                  <bdi>{S.update.wrongOperation}</bdi>
                </InfoNote>
              )}
            </section>
          )}

          {/* ONLY FOR A RENEWAL. An update keeps the licence it edits, and its users with it,
              so offering to copy them is offering a choice that does not exist — which is how
              the legacy screen behaves too. */}
          {preview?.operationType === 'renew' && (
            <fieldset className="license__options">
              <legend>
                <bdi>{S.update.renewOptions}</bdi>
              </legend>
              {[
                { value: 'no-copy', label: S.update.noCopy },
                { value: 'copy', label: S.update.copy },
              ].map((option) => (
                <label key={option.value} className="license__option">
                  <input
                    type="radio"
                    name="renewOption"
                    value={option.value}
                    checked={renewOption === option.value}
                    onChange={() => setRenewOption(option.value)}
                  />
                  <bdi>{option.label}</bdi>
                </label>
              ))}
            </fieldset>
          )}
        </form>
      </Modal>
    </>
  )
}
